import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { TabControlComponent } from '../../shared/controls/tab-control/tab-control.component';
import { TabPageComponent } from '../../shared/controls/tab-control/tab-page.component';
import { ReportParameter } from '../../shared/models/report-parameter.model';
import { ReportConstants } from '../../shared/constants/report.constants';
import { AutoCompleteDropdownComponent } from '../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';

import { ErrorService } from '../../shared/services/error-service';
import { ToastService } from '../../shared/toast/toast.service';

import { ActiveReportsResult, AllPeriodItem, ClientAreaService, ClientService, DeclarationReportPackInput, PeriodService, ReportPackDeclarationService } from '../../../service/proxy';


@Component({
    selector: 'declaration-report-selection',
    templateUrl: './declaration-report-selection.component.html',
    styleUrls: ['./declaration-report-selection.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        AutoCompleteDropdownComponent,
        TabControlComponent,
        TabPageComponent,
    ],

    providers: [
        PeriodService,
        ClientService,
        ClientAreaService,
        ReportPackDeclarationService,
    ]
})
export class DeclarationReportSelectionComponent implements OnInit {
    private readonly REPORT_PACK_CODE = -1;

    public isSaving: boolean = false;
    public formSubmitted: boolean = false;

    public periods: AllPeriodItem[] = [];
    public selectedPeriodId: number = 0;

    public clients: { id: number; name: string; }[] = [];
    public selectedClientId: number = 0;

    public clientAreas: { id: number; name: string; }[] = [];
    public selectedClientAreaId: number = 0;

    public reports: { id: number; name: string; linkName: string }[] = [];
    public selectedReportId: number = 0;

    public startDate: string = '';
    public endDate: string = '';

    constructor(
        private router: Router,
        private errorService: ErrorService,
        private toastService: ToastService,
        private periodService: PeriodService,
        private clientService: ClientService,
        private clientAreaService: ClientAreaService,
        private reportPackService: ReportPackDeclarationService,
    ) {}

    async ngOnInit(): Promise<void> {
        this.getPeriodsForLookup();
        this.getClientsForLookup();

        // Handle whether the user clicked back from existing report selection
        await this.applyIncomingReportParams();
    }

    getPeriodsForLookup(): void {
        this.periodService.getAllPeriods()
            .subscribe((data) => {
                this.periods = data.results || [];
            }
        );
    }

    getClientsForLookup(): void {
        this.clientService.getAllClients()
            .subscribe({
                next: (response) => {
                    this.clients = response.results.map(client => ({
                        id: client.id,
                        name: client.clientName
                    }));
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            }
        );
    }

    private async applyIncomingReportParams(): Promise<void> {

        // If user navigated from report viewer with reportParams, prefill selection controls
        const incomingParams: any[] = (window.history.state && Array.isArray(window.history.state.reportParams))
            ? window.history.state.reportParams
            : [];

        if (!incomingParams || !Array.isArray(incomingParams) || incomingParams.length === 0) {
            return;
        }

        try {
            const periodParam = incomingParams.find((p: any) => p.label === 'Period');
            if (periodParam?.key) {
                this.selectedPeriodId = Number(periodParam.key);
            }

            const clientParam = incomingParams.find((p: any) => p.label === 'Client');
            if (clientParam?.key) {
                this.selectedClientId = Number(clientParam.key);
                // await client areas being loaded before using client area param
                await this.setClientParameter(this.selectedClientId);

                const clientAreaParam = incomingParams.find((p: any) => p.label === 'Client Area');
                if (clientAreaParam?.key) {
                    this.selectedClientAreaId = clientAreaParam.key;
                    this.getReportsForLookup();
                }
            }

        }
        catch (e) {
            console.warn('Failed to apply incomingParams reportParams', e);
        }
    }

    onPeriodSelectionChange(selectedId: number | null): void {
        this.selectedPeriodId = selectedId ?? 0;
    }

    onClientSelectionChange(selectedId: number | null): void {
        this.selectedClientId = selectedId ?? 0;
        this.selectedClientAreaId = 0;
        this.clientAreas = [];
        this.reports = [];
        this.getClientAreasForLookup();
    }

    onClientAreaSelectionChange(selectedId: number | null): void {
        this.selectedClientAreaId = selectedId ?? 0;
        this.selectedReportId = 0;
        this.getReportsForLookup();
    }

    onReportSelectionChange(selectedId: number | null): void {
        this.selectedReportId = selectedId ?? 0;
    }

    displayPeriod(option: any): string {
        const paddedMonth = ('0' + option.month).slice(-2);
        return `${option.year}-${paddedMonth}`;
    }

    setClientParameter(selectedId: number | null): Promise<void> {
        this.selectedClientId = selectedId ?? 0;
        return this.getClientAreasForLookupAsync();
    }

    private getClientAreasForLookupAsync(): Promise<void> {
        if (!this.selectedClientId) {
            this.clientAreas = [];
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            this.clientAreaService.getClientAreaLookup(this.selectedClientId)
                .subscribe({
                    next: (clientAreas) => {
                        if (!clientAreas.length) {
                            this.toastService.show('No policies found for the selected client', 'info');
                        }

                        this.clientAreas = clientAreas.map(ca => ({
                            id: ca.id,
                            name: ca.name
                        }));
                        resolve();
                    },
                    error: (error) => {
                        this.toastService.show('Failed to load client areas', 'error');
                        reject(error);
                    }
                }
            );
        });
    }

    getClientAreasForLookup(): void {
        if (!this.selectedClientId) {
            this.clientAreas = [];
            return;
        }

        this.clientAreaService.getClientAreaLookup(this.selectedClientId)
            .subscribe({
                next: (clientAreas) => {
                    if (!clientAreas.length) {
                        this.toastService.show('No policies found for the selected client', 'info');
                        return;
                    }

                    this.clientAreas = clientAreas.map(ca => ({
                        id: ca.id,
                        name: ca.name
                    }));
                },
                error: () => {
                    this.toastService.show('Failed to load client areas', 'error');
                }
            }
        );
    }

    getReportsForLookup(): void {
        if (!this.selectedClientAreaId) {
            this.reports = [];
            return;
        }

        this.clientAreaService
            .getActiveReports(this.selectedClientAreaId, undefined, 1, 100, undefined, undefined)
            .subscribe((response: ActiveReportsResult) => {
                // 1) map
                let reports = response.results.map(r => ({
                    id: r.id,
                    name: r.reportName,
                    linkName: r.linkName
                }));

                // 2) remove general duplicates (by linkName fallback name/id)
                const seen = new Set<string>();
                reports = reports.filter(r => {
                    // Exclude Declaration Report Pack by name
                    if ((r.name || '').trim().toLowerCase() === 'declaration report pack') {
                        return false;
                    }
                    const key = (r.linkName || r.name || String(r.id)).toLowerCase();
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });

                // 3) upsert canonical Report Pack
                const canonicalPack = {
                    id: this.REPORT_PACK_CODE,
                    name: 'Report Pack',
                    linkName: ReportConstants.ReportPack
                };

                const isPack = (x: any) =>
                    (x.name?.trim().toLowerCase() === 'report pack') ||
                    (x.linkName === ReportConstants.ReportPack);

                const idx = reports.findIndex(isPack);

                if (idx >= 0) {
                    // update the first occurrence
                    reports[idx] = { ...reports[idx], ...canonicalPack };
                    // remove any other duplicates of Report Pack
                    reports = reports.filter((r, i) => i === idx || !isPack(r));
                } else {
                    // not present → add it
                    reports.push(canonicalPack);
                }

                this.reports = reports;
            });
    }

    onRunReport(form: any): void {
        this.formSubmitted = true;

        if (form.invalid || !this.selectedPeriodId || !this.selectedClientAreaId || !this.selectedReportId) {
            return;
        }

        const selectedPeriod = this.periods.find(p => p.id === this.selectedPeriodId);

        const periodValue = selectedPeriod?.month && selectedPeriod.month < 10
            ? `${selectedPeriod.year}-0${selectedPeriod.month}`
            : `${selectedPeriod?.year}-${selectedPeriod?.month}`;

        const selectedClientArea = this.clientAreas.find(c => c.id === this.selectedClientAreaId);
        const clientAreaValue = selectedClientArea?.name || '';

        const selectedClient = this.clients.find(c => c.id === this.selectedClientId);
        const clientValue = selectedClient?.name || '';

        const selectedReport = this.reports.find(r => r.id === this.selectedReportId);
        const reportCode = (ReportConstants as any)[selectedReport?.linkName || ''];

        const reportParams = [
            new ReportParameter(0, 'Period', this.selectedPeriodId, periodValue),
            new ReportParameter(1, 'Client Area', this.selectedClientAreaId, clientAreaValue),
            new ReportParameter(2, 'Client', this.selectedClientId, clientValue),
        ];

        if (this.selectedReportId === this.REPORT_PACK_CODE) {
            this.createReportPack(form);
        }
        else {
            this.router.navigate(['/report-viewer', reportCode], {
                state: { reportParams }
            });
        }
    }

    createReportPack(form: any): void {
        if (!form.valid || this.isSaving) {
            return;
        }

        const input = new DeclarationReportPackInput ({
            periodId: this.selectedPeriodId,
            clientId: this.selectedClientId,
            clientAreaId: this.selectedClientAreaId,
            emailAddress: undefined,
            reportStatus: undefined,
            dateSent: undefined,
            updatedAt: undefined,
            deletedAt: undefined,
            createdAt: new Date(),
        });

        this.isSaving = true;

        this.reportPackService.createDeclarationReportPack(input)
            .subscribe({
                next: () => {
                    this.toastService.show('Declaration Report Pack requested. Email will follow.', 'success');
                    form.resetForm();
                },
                error: (err: any) => this.errorService.handleError(err),
                complete: () => this.isSaving = false
            }
        );
    }
}